// Intercom deep-link URL utility functions following existing service patterns
// Following existing utility patterns from featureUrlService.ts and faqUrlService.ts

import { isBrowser } from '@utils';

/**
 * Query param that deep-links to a fresh Intercom conversation. Visiting any
 * non-embeddable page with this param opens a new chat (see Layout deep-link
 * handler). This is the canonical wire format used by the shareable link
 * (e.g. https://hyperhuman.cc/?ask=1).
 */
export const INTERCOM_ASK_PARAM = 'ask';

/**
 * True when a CMS `button_link` is the Intercom ask wire format (`?ask=1`).
 * Used by the shared CTA footer so that value opens Messenger in-place
 * instead of being treated as an internal Gatsby path (getLocalizedLink
 * would otherwise turn `?ask=1` into `/?ask=1` / `/{locale}/?ask=1`).
 */
export const isIntercomAskLink = (link?: string): boolean => {
    if (!link || typeof link !== 'string') return false;
    const t = link.trim();
    if (!t.startsWith('?')) return false;
    try {
        return new URLSearchParams(t.slice(1)).has(INTERCOM_ASK_PARAM);
    } catch {
        return false;
    }
};

/**
 * Reads the current `?ask` query param.
 * Following existing isBrowser patterns from featureUrlService.ts.
 * @returns The ask parameter from the current URL, or null
 */
export const getCurrentAskParam = (): string | null => {
    if (!isBrowser()) return null;

    try {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(INTERCOM_ASK_PARAM);
    } catch {
        return null;
    }
};

/**
 * Returns the search string with the `?ask` param stripped, preserving every
 * other param (utm_*, mobileApp, locale cache-bust, etc). Used to clean the URL
 * after the chat opens so refresh/back will not reopen it. Mirrors
 * getSearchWithoutCacheBustParam in websiteUrlUtils.ts.
 * @param search - Raw search string (e.g. from window.location.search)
 * @returns Search string without `ask`, or empty string
 */
export const getSearchWithoutAskParam = (search: string): string => {
    if (!search || !search.startsWith('?')) return '';
    const params = new URLSearchParams(search);
    params.delete(INTERCOM_ASK_PARAM);
    const filtered = params.toString();
    return filtered ? `?${filtered}` : '';
};
